/**
 * Per-channel inbound coalescing buffers.
 *
 * The rule they exist to enforce (plan §2.2): **a WS frame never calls
 * `setState`.** It lands here, synchronously and cheaply, and one
 * `requestAnimationFrame` flush per frame drains every channel into the stores.
 * The socket must keep draining — a client that stops reading gets
 * `SLOW_CONSUMER` and loses its subscription (docs/04 §4.3) — so `push` is
 * always O(1) and never does work that could block the read loop.
 *
 * Three coalescing modes, one per channel shape:
 *
 * - `latest` — every frame is a complete replacement, so only the newest
 *   matters. `ticker` (which carries no `seq` at all), `index`, `heartbeat`.
 * - `queue` — every frame is a distinct event and dropping one loses
 *   information. `trades`, `fills`, `settlements`, `notifications`, and book
 *   deltas, whose `seq` continuity is the whole recovery mechanism.
 * - `keyed` — frames supersede each other per key, newest wins. `orders` keyed
 *   by `order_id`, `positions` by `market_id`, `candles` by `open_ms`.
 *
 * A `queue` buffer that overflows **drops the oldest and counts it**. It is
 * always wrong to grow without bound (that is the render thread dying instead
 * of one channel degrading) and always wrong to drop silently: a non-zero
 * `dropped` invalidates `seq` continuity, and the sequencer's answer to that is
 * a REST re-snapshot, exactly as if the server had gapped.
 */

export type CoalesceMode = 'latest' | 'queue' | 'keyed'

export interface ChannelBufferStats {
  /** Frames accepted since construction. */
  received: number
  /** Frames the flush never saw — superseded by coalescing, or dropped on overflow. */
  coalesced: number
  /** Frames lost to overflow specifically. Non-zero means re-snapshot. */
  dropped: number
}

export interface BufferOptions<T> {
  mode: CoalesceMode
  /** `queue`: entries before the oldest is dropped. `keyed`: distinct keys. */
  capacity?: number
  /** Required for `keyed`. */
  keyOf?: (value: T) => string
}

export const DEFAULT_QUEUE_CAPACITY = 4096

/**
 * One channel's buffer. Drains to an array in arrival order in every mode, so
 * the flush has a single shape to consume.
 */
export class ChannelBuffer<T> {
  readonly mode: CoalesceMode
  readonly #capacity: number
  readonly #keyOf: ((value: T) => string) | undefined

  #latest: T | undefined
  #hasLatest = false
  #queue: T[] = []
  #keyed = new Map<string, T>()

  #received = 0
  #coalesced = 0
  #dropped = 0

  constructor(options: BufferOptions<T>) {
    this.mode = options.mode
    this.#capacity = options.capacity ?? DEFAULT_QUEUE_CAPACITY
    this.#keyOf = options.keyOf
    if (this.mode === 'keyed' && !this.#keyOf) {
      throw new Error('a keyed buffer needs keyOf — the dedupe key is the channel\'s, not the buffer\'s guess')
    }
  }

  get pending(): boolean {
    return this.size > 0
  }

  get size(): number {
    switch (this.mode) {
      case 'latest':
        return this.#hasLatest ? 1 : 0
      case 'queue':
        return this.#queue.length
      case 'keyed':
        return this.#keyed.size
    }
  }

  get stats(): ChannelBufferStats {
    return { received: this.#received, coalesced: this.#coalesced, dropped: this.#dropped }
  }

  /** True once anything has been lost. The sequencer re-snapshots on this the
   *  same way it re-snapshots on a `seq` gap — the book is equally wrong either way. */
  get lossy(): boolean {
    return this.#dropped > 0
  }

  push(value: T): void {
    this.#received++
    switch (this.mode) {
      case 'latest': {
        if (this.#hasLatest) this.#coalesced++
        this.#latest = value
        this.#hasLatest = true
        return
      }
      case 'queue': {
        if (this.#queue.length >= this.#capacity) {
          this.#queue.shift()
          this.#dropped++
          this.#coalesced++
        }
        this.#queue.push(value)
        return
      }
      case 'keyed': {
        const key = this.#keyOf!(value)
        if (this.#keyed.has(key)) {
          this.#coalesced++
          // Delete first so re-inserting moves the key to the end and the drain
          // stays in *last-touched* order, which is what a renderer wants.
          this.#keyed.delete(key)
        } else if (this.#keyed.size >= this.#capacity) {
          const oldest = this.#keyed.keys().next()
          if (!oldest.done) {
            this.#keyed.delete(oldest.value)
            this.#dropped++
          }
        }
        this.#keyed.set(key, value)
        return
      }
    }
  }

  /** Take everything and reset. Never returns a live reference to internal state. */
  drain(): T[] {
    switch (this.mode) {
      case 'latest': {
        if (!this.#hasLatest) return []
        const out = [this.#latest as T]
        this.#latest = undefined
        this.#hasLatest = false
        return out
      }
      case 'queue': {
        const out = this.#queue
        this.#queue = []
        return out
      }
      case 'keyed': {
        const out = [...this.#keyed.values()]
        this.#keyed.clear()
        return out
      }
    }
  }

  /** Drop everything pending without delivering it — a subscription that just
   *  died has a buffer full of frames belonging to a book we are about to refetch. */
  clear(): void {
    this.#latest = undefined
    this.#hasLatest = false
    this.#queue = []
    this.#keyed.clear()
  }

  resetStats(): void {
    this.#received = 0
    this.#coalesced = 0
    this.#dropped = 0
  }
}

/**
 * The set of live buffers, one per subscribed channel.
 *
 * Keyed by channel name rather than `sub_id`, because `sub_id`s do not survive
 * a reconnect (docs/03 §4.2) and a buffer keyed on one would silently orphan
 * itself the moment the socket came back.
 */
export class BufferSet {
  readonly #buffers = new Map<string, ChannelBuffer<unknown>>()

  channel<T>(name: string, options: BufferOptions<T>): ChannelBuffer<T> {
    const existing = this.#buffers.get(name)
    if (existing) return existing as ChannelBuffer<T>
    const created = new ChannelBuffer<T>(options)
    this.#buffers.set(name, created as ChannelBuffer<unknown>)
    return created
  }

  get(name: string): ChannelBuffer<unknown> | undefined {
    return this.#buffers.get(name)
  }

  delete(name: string): void {
    this.#buffers.get(name)?.clear()
    this.#buffers.delete(name)
  }

  get channelNames(): string[] {
    return [...this.#buffers.keys()]
  }

  /** Channels with something to deliver. The flush walks only these. */
  pendingChannels(): string[] {
    const out: string[] = []
    for (const [name, buffer] of this.#buffers) if (buffer.pending) out.push(name)
    return out
  }

  /** Channels that have lost a frame and therefore need a REST re-snapshot. */
  lossyChannels(): string[] {
    const out: string[] = []
    for (const [name, buffer] of this.#buffers) if (buffer.lossy) out.push(name)
    return out
  }

  clear(): void {
    for (const buffer of this.#buffers.values()) buffer.clear()
  }
}
